// Locale-aware cart URL helper.
// Shopify injects window.Shopify.routes.root via content_for_header; on a
// localized page it contains the locale prefix (e.g. "/it/"). Hitting
// `${root}cart` ensures section-render fetches return markup in the
// active storefront locale, fixing the language flip reported in Card #496.
// Falls back to the legacy non-locale-aware routes.cart_url when the global
// is missing so behaviour stays identical on older surfaces.
function localeAwareCartUrl() {
  if (
    typeof window !== 'undefined' &&
    window.Shopify &&
    window.Shopify.routes &&
    typeof window.Shopify.routes.root === 'string'
  ) {
    const root = window.Shopify.routes.root;
    return (root.endsWith('/') ? root : root + '/') + 'cart';
  }
  if (typeof routes !== 'undefined' && routes && routes.cart_url) {
    return routes.cart_url;
  }
  return '/cart';
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelectorAll('.cart-open-trigger');
    cartLink.forEach((links) => {
      links.setAttribute('role', 'button');
      links.setAttribute('aria-haspopup', 'dialog');
      links.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(links);
      });
      links.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(links);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // GA4 view_cart (Card #2) — user-initiated opens only (cart-icon click);
    // NOT the auto-open after add-to-cart (open() called with no arg).
    if (triggeredBy && window.cfPushViewCart) window.cfPushViewCart();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }
    this.productId = parsedState && parsedState.id;

    // Mirror cart.js wrapper-level is-empty toggle so focus-trap resolves
    // the right container after empty → non-empty transition.
    if (parsedState && typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    let sectionsApplied = true;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML =
        parsedState && parsedState.sections
          ? parsedState.sections[section.id]
          : null;
      if (!sectionElement || !sectionHTML) {
        sectionsApplied = false;
        return;
      }
      try {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );
      } catch (e) {
        console.warn('CartDrawer.renderContents: section parse failed', e);
        sectionsApplied = false;
      }
    });

    if (!sectionsApplied) {
      try {
        await this.refreshFromSectionFetch();
      } catch (e) {
        console.error('CartDrawer.renderContents: section refetch failed', e);
      }
    }

    setTimeout(() => {
      // Only re-attach overlay handler when the sync path replaced the
      // overlay node; in the fallback path the node was not touched and
      // its constructor-installed handler is still active.
      if (sectionsApplied) {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }
      }
      this.open();
    });
  }

  /**
   * Re-fetch full drawer markup when an upstream caller (e.g. BOLD Options)
   * delivers a /cart/add.js response without sections data.
   * Mirrors the pattern in CartDrawerItems.refreshFromSectionFetch().
   */
  refreshFromSectionFetch() {
    const sectionId = 'gmv-cart-drawer';
    return fetch(`${localeAwareCartUrl()}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
        const destInner = document.querySelector('#CartDrawer .drawer__inner');
        if (sourceInner && destInner) {
          destInner.innerHTML = sourceInner.innerHTML;
        }
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'gmv-cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /** Section used for /cart/change.js partials and for onCartUpdate fetches (must match theme drawer). */
  getDrawerSectionId() {
    return this.dataset.cartRefreshSection || 'gmv-cart-drawer';
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: this.getDrawerSectionId(),
        selector: '.drawer__inner',
      },
    ];
  }

  /**
   * Re-fetch full drawer markup when /cart/change.js returns null sections (common with apps).
   */
  refreshFromSectionFetch() {
    const sectionId = this.getDrawerSectionId();
    return fetch(`${localeAwareCartUrl()}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceInner = html.querySelector('#CartDrawer .drawer__inner');
        const destInner = document.querySelector('#CartDrawer .drawer__inner');
        if (sourceInner && destInner) {
          destInner.innerHTML = sourceInner.innerHTML;
          // This swap recreates <cart-drawer-items>; restore the buyer's latest
          // intent on the fresh inputs so a mid-burst repaint doesn't regress.
          this.reapplyPendingOptimistic();
          return;
        }
        const sourceQty = html.querySelector('cart-drawer-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
          this.reapplyPendingOptimistic();
        }
      });
  }

  onCartUpdate() {
    const sectionId = this.getDrawerSectionId();
    fetch(`${localeAwareCartUrl()}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-drawer-items');
        if (!sourceQty) {
          console.warn(
            'CartDrawerItems.onCartUpdate: missing <cart-drawer-items> in section',
            sectionId
          );
          return;
        }
        this.innerHTML = sourceQty.innerHTML;
        // Restore latest intent after a pubsub repaint (see reapply rationale).
        this.reapplyPendingOptimistic();
        // Always resync the header/nav .cart-count from the freshly-rendered
        // drawer, even with no pending qty edit (e.g. add-to-cart / remove from
        // another surface) — otherwise the header badge stays stale until a
        // full page refresh.
        this.refreshOptimisticCount();

        const cartEvent = new CustomEvent('cartUpdated');
        cartEvent.initCustomEvent('cart:updated', true, true, null);
        document.dispatchEvent(cartEvent);

        const cartNoteEl = document.querySelector('#Cart-note');
        if (cartNoteEl) {
          const body = JSON.stringify({ note: cartNoteEl.value });
          fetch(`${routes.cart_update_url}`, {
            ...fetchConfig(),
            ...{ body },
          }).then(() => {});
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
